// src/services/DocumentCostService.js

import { BaseCostService, DEFAULT_COST_CONFIG } from './BaseCostService';

const DOCUMENT_SNARKY_QUIPS = [
  { threshold: 300, quip: "A modest investment in collective procrastination reading." },
  { threshold: 800, quip: "Roughly the cost of a good technical book — but with more meetings about it." },
  { threshold: 1500, quip: "That's a lot of coffee breaks while people pretend to read documentation." },
  { threshold: 3000, quip: "You could've hired a technical writer for that amount." },
  { threshold: 5000, quip: "Congratulations: this document costs more than most people's monthly salary." },
  { threshold: 8000, quip: "At this price, the document should write itself and make everyone tea." },
  { threshold: 15000, quip: "This document now has its own budget line and probably needs a project manager." },
  { threshold: Infinity, quip: "You've created the most expensive piece of documentation in company history. Frame it." },
];

export class DocumentCostService extends BaseCostService {
  constructor(config = DEFAULT_COST_CONFIG) {
    super(config);
  }

  calculateCost({ 
    authors, 
    reviewers, 
    readers, 
    writingTimeHours, 
    reviewTimeHours, 
    readingTimeHours, 
    reviewCycles 
  }) {
    this.validateInput({ 
      authors, 
      reviewers, 
      readers, 
      writingTimeHours, 
      reviewTimeHours, 
      readingTimeHours, 
      reviewCycles 
    });

    const writingCost = this.calculateActivityCost(authors, writingTimeHours);
    const reviewCost = this.calculateActivityCost(reviewers, reviewTimeHours * reviewCycles);
    const readingCost = this.calculateActivityCost(readers, readingTimeHours);

    const totalCost = Math.round(writingCost + reviewCost + readingCost);

    return {
      totalCost,
      breakdown: {
        writingCost: Math.round(writingCost),
        reviewCost: Math.round(reviewCost),
        readingCost: Math.round(readingCost)
      },
      snarkyQuip: this.getSnarkyQuip(totalCost),
    };
  }

  calculateActivityCost(participants, timeHours) {
    if (!participants || participants.length === 0 || timeHours === 0) {
      return 0;
    }

    let cost = 0;
    participants.forEach(({ count, hourlyRate }) => {
      const trueCostPerHour = this.calculateTrueCostPerHour(hourlyRate);
      cost += count * trueCostPerHour * timeHours;
    });

    return cost;
  }

  validateInput({ 
    authors, 
    reviewers, 
    readers, 
    writingTimeHours, 
    reviewTimeHours, 
    readingTimeHours, 
    reviewCycles 
  }) {
    // Validate arrays
    if (!Array.isArray(authors)) {
      throw new Error('Authors must be an array');
    }
    if (!Array.isArray(reviewers)) {
      throw new Error('Reviewers must be an array');
    }
    if (!Array.isArray(readers)) {
      throw new Error('Readers must be an array');
    }

    // Validate time inputs
    if (typeof writingTimeHours !== 'number' || writingTimeHours < 0) {
      throw new Error('Writing time must be a non-negative number');
    }
    if (typeof reviewTimeHours !== 'number' || reviewTimeHours < 0) {
      throw new Error('Review time must be a non-negative number');
    }
    if (typeof readingTimeHours !== 'number' || readingTimeHours < 0) {
      throw new Error('Reading time must be a non-negative number');
    }
    if (typeof reviewCycles !== 'number' || reviewCycles < 0) {
      throw new Error('Review cycles must be a non-negative number');
    }

    // Validate participant objects
    const allParticipants = [...authors, ...reviewers, ...readers];
    allParticipants.forEach((participant, index) => {
      if (typeof participant.count !== 'number' || participant.count < 0) {
        throw new Error(`Participant ${index} count must be a non-negative number`);
      }
      if (typeof participant.hourlyRate !== 'number' || participant.hourlyRate < 0) {
        throw new Error(`Participant ${index} hourly rate must be a non-negative number`);
      }
    });
  }


  getSnarkyQuip(cost) {
    const quip = DOCUMENT_SNARKY_QUIPS.find(({ threshold }) => cost < threshold);
    return quip ? quip.quip : DOCUMENT_SNARKY_QUIPS[DOCUMENT_SNARKY_QUIPS.length - 1].quip;
  }

}

// Export default instance for backward compatibility
export const documentCostService = new DocumentCostService();